// Pure helpers for the AGENT config-approval prefill flow (Slack deep link
// `/{owner}/agents/{agentId}/settings?run_id=42`). Kept free of React/query so
// they can be unit-tested in isolation (see `config-prefill.test.ts`).

/**
 * Coerce a raw search-param value into a positive run id, or `undefined`.
 *
 * URL query params arrive as strings (`?run_id=42`), but a programmatic
 * navigation could pass a number. Anything that isn't a finite positive integer
 * (`0`, negatives, `"abc"`, arrays) is rejected so a bogus link never opens the
 * review flow.
 */
export function parseRunId(raw: unknown): number | undefined {
	const n = typeof raw === "string" ? Number(raw) : raw;
	if (typeof n !== "number" || !Number.isInteger(n) || n <= 0) {
		return undefined;
	}
	return n;
}

/**
 * Render mode for a `?run_id=` review deep link, derived from the
 * pending-proposal query state. The key invariant is FAIL CLOSED: a review link
 * must never fall through to the editable Save/Delete form on a loading or
 * errored query — only an explicit "gone" (404, run resolved/absent) restores
 * normal editing.
 *   - "none"    → not a review (no run_id) → normal form.
 *   - "loading" → proposal in flight → hold controls/footer.
 *   - "error"   → query failed → block editing, offer retry.
 *   - "gone"    → explicit 404 → normal form + "no longer pending" notice.
 *   - "active"  → proposal loaded → prefilled read-only review + Approve/Reject.
 */
export type ReviewMode = "none" | "loading" | "error" | "gone" | "active";

export function resolveReviewMode(params: {
	isReview: boolean;
	isLoading: boolean;
	isError: boolean;
	status: "pending" | "gone" | undefined;
}): ReviewMode {
	if (!params.isReview) return "none";
	if (params.status === "pending") return "active";
	if (params.status === "gone") return "gone";
	if (params.isError) return "error";
	// No result yet and not an explicit gone/error → treat as loading (fail
	// closed: never expose the editable form while the proposal is unknown).
	return "loading";
}

/** The three agent identity/settings fields the builder proposal can carry. */
interface AgentPrefillOverlay {
	name?: string;
	description?: string;
	identityMd?: string;
}

/**
 * Map a flat agent proposal (`{ action, agent_id, name, description,
 * identity_md, ... }`) onto the form fields to overlay. Only keys the proposal
 * actually contains are returned, so a partial proposal (e.g. name-only) leaves
 * the other fields on their server-hydrated values. String coercion guards
 * against non-string JSON values; anything else for a key is skipped.
 */
export function overlayFromProposal(
	proposal: Record<string, unknown> | null | undefined,
): AgentPrefillOverlay {
	const overlay: AgentPrefillOverlay = {};
	if (!proposal) return overlay;
	if (typeof proposal.name === "string") overlay.name = proposal.name;
	if (typeof proposal.description === "string") {
		overlay.description = proposal.description;
	}
	if (typeof proposal.identity_md === "string") {
		overlay.identityMd = proposal.identity_md;
	}
	return overlay;
}
